// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ApproveConnectionRequestSchema, AuthCategorySchema, AuthConfigSchema, CredentialTestOutcomeSchema, CatalogListResponseSchema, ConnectionRequestQueueItemSchema, ConnectionRequestStatusSchema, DenyConnectionRequestSchema, GrantableIntegrationSummarySchema, CONNECTED_DB_AUTH_MODES, CONNECTED_DB_ENGINES, CONNECTED_DB_ENV_PREFIX_MAX_LENGTH, CredentialAuthConfigSchema, CredentialRequiredGroupRefSchema, IntegrationApprovalModeSchema, IntegrationAuthModeSchema, IntegrationCredentialSchema, IntegrationDeliveryModeSchema, IntegrationListItemSchema, IntegrationSchema, IntegrationStatusSchema, IntegrationTlsConfigSchema, PaginationQuerySchema, UpdateIntegrationResponseSchema, isInjectableConnectedDbAuthMode, isSecretlessAuthMode, paginated, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
const IntegrationIdParamsSchema = z.object({ id: z.string().uuid() });
const ConnectionRequestIdParamsSchema = z.object({ id: z.string().uuid() });
// Addresses one credential on an integration by its stable slug (same grammar as
// CredentialInputSchema.slug). Used by the replace-secret op's path.
const CredentialSlugParamsSchema = IntegrationIdParamsSchema.extend({
    slug: z
        .string()
        .min(1)
        .max(63)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'lowercase alphanumeric and hyphens'),
});
// A credential supplied at registration or via add-credential: a stable slug,
// a free-text scope, and a write-only secret. The secret is never echoed back.
// It is omitted only for a `workload_identity` connected database — the proxy
// authenticates as the platform data identity, so there is nothing to vault
// (ADR-0030); the create refinements and the add-credential service enforce
// presence for every other mode. `authConfig` is the upstream OAuth client a
// `user_delegated` credential presents (docs/48 §Catalog profiles and
// credentials); the create refinements and the service require it on that mode
// and refuse it on every other. Strict: an unknown field fails validation
// rather than being silently stripped, so a caller can never believe it set a
// governance field the contract does not carry (ADR-0039).
export const CredentialInputSchema = z.strictObject({
    slug: z
        .string()
        .min(1)
        .max(63)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'lowercase alphanumeric and hyphens'),
    scope: z.string().min(1).max(2000),
    secret: z.string().min(1).max(4096).optional(),
    approvalMode: IntegrationApprovalModeSchema.optional(),
    requiredGroups: z.array(CredentialRequiredGroupRefSchema).max(20).optional(),
    authConfig: CredentialAuthConfigSchema.optional(),
});
// Injected env var names must match the managed-name grammar and not collide
// with the reserved set (docs/29 § Reserved Names); the platform enforces the
// collision check, the schema enforces the grammar.
const EnvVarNameSchema = z.string().regex(/^[A-Z][A-Z0-9_]{0,127}$/);
const isHttpUrl = (value) => {
    try {
        const { protocol } = new URL(value);
        return protocol === 'https:' || protocol === 'http:';
    }
    catch {
        return false;
    }
};
// A base URL may be plain HTTP only on a service_account integration whose
// create or edit carries allowInsecureHttp: true; the service enforces both,
// since an edit's auth mode lives on the stored row (docs/09 §Generic REST integration).
const BaseUrlSchema = z.string().url().refine(isHttpUrl, 'must use HTTPS or HTTP');
// Connected-database target (ADR-0030): where the proxy's SQL-execution branch
// connects. Registrable engines are the MVP slice of the executor's dialect seam.
export const ConnectedDbConnectionSchema = z.object({
    engine: z.enum(CONNECTED_DB_ENGINES),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    database: z.string().min(1).max(255),
});
const isConnectedDbAuthMode = (mode) => CONNECTED_DB_AUTH_MODES.includes(mode ?? '');
// A connected-DB (or system) authConfig carries exactly the mode's non-secret
// params. The schema is passthrough (HTTP modes need open keys, e.g. multi_header
// params), so without this allowlist a pasted client_secret/password would ride
// into the auth_config column in plaintext; secrets belong in credentials[].secret.
const CONNECTED_DB_AUTH_CONFIG_KEYS = {
    workload_identity: [],
    sql_password: ['username'],
    service_principal: ['client_id', 'tenant_id'],
    system: [],
};
// A connected database that acts as the signed-in user (a Fabric Warehouse) carries
// the per-user OAuth mechanism params; the client secret is credentials[].secret.
export const USER_DELEGATED_DB_AUTH_CONFIG_KEYS = [
    'mechanism',
    'authorize_url',
    'token_url',
    'revoke_url',
    'scopes',
    'token_endpoint_auth_method',
];
// Engines whose connected database may act as the signed-in user (docs/48
// §Microsoft Fabric and Power BI).
const USER_DELEGATED_DB_ENGINES = ['fabric-warehouse'];
function connectedDbAuthConfigKeys(authCategory, authMode) {
    if (authCategory === 'connected-database' && authMode === 'user_delegated') {
        return USER_DELEGATED_DB_AUTH_CONFIG_KEYS;
    }
    return CONNECTED_DB_AUTH_CONFIG_KEYS[authMode ?? ''];
}
// Registration atomically creates the integration and its initial credential(s):
// a finished registration never leaves an integration without credentials
// (docs/28 § /integrations). `deliveryMode` defaults to `proxied`; `injected`
// requires an `envVarName`. Request keys are camelCase like the thin DB row;
// the read projection (IntegrationListItem) stays snake_case.
//
// Two registration shapes share this schema. HTTP integrations carry `baseUrl`
// + an HTTP `authMode`. A connected database (`authCategory:
// 'connected-database'`) instead carries `connection` (engine/host/port/database)
// + a database `authMode`, and is `proxied` — the data proxy runs its statements
// server-side (ADR-0030) — unless a `service_principal` is delivered `injected`,
// where `envVarName` is the prefix of the per-field env vars. The cross-field
// rules below enforce each shape.
//
// `catalogKey` records the integration-catalog entry IT registered from (ADR-0026);
// it is omitted for a Custom (catalog-less) registration. `authConfig` carries the
// params-only, mode-specific auth params (service_account → auth_inject;
// client_credentials → token_url/scopes; sql_password → username); it never
// repeats `authMode`.
export const CreateIntegrationSchema = z
    .object({
    // `~` separates an integration from a credential in the proxy path (ADR-0089).
    name: z
        .string()
        .min(1)
        .max(63)
        .refine((name) => !name.includes('~'), 'must not contain ~'),
    displayName: z.string().min(1).max(255),
    catalogKey: z.string().max(63).optional(),
    baseUrl: BaseUrlSchema.optional(),
    allowInsecureHttp: z.boolean().optional(),
    tlsConfig: IntegrationTlsConfigSchema.nullable().optional(),
    authMode: IntegrationAuthModeSchema.optional(),
    authConfig: AuthConfigSchema.optional(),
    kind: z.string().max(120).optional(),
    authCategory: AuthCategorySchema.optional(),
    deliveryMode: IntegrationDeliveryModeSchema.default('proxied'),
    envVarName: EnvVarNameSchema.optional(),
    connection: ConnectedDbConnectionSchema.optional(),
    credentials: z.array(CredentialInputSchema).min(1),
    // Optional first Knowledge entry (T-8794) — IT knows most about why this
    // integration is being added and how apps should use it right now, at
    // registration. Builder-facing context (schema, idiomatic usage, gotchas),
    // never credential-acquisition guidance. Omitted/empty creates no entry —
    // registration seeds no Knowledge on its own (docs/32 AC-KB-02).
    knowledgeBodyMd: z.string().min(1).max(100_000).optional(),
    // Required while the org has not yet accepted the data-access terms (docs/09 §Scope Model); ignored once it has.
    acceptDataAccessTerms: z.literal(true).optional(),
})
    .refine((v) => v.deliveryMode !== 'injected' || v.envVarName !== undefined, {
    message: 'envVarName is required when deliveryMode is injected',
    path: ['envVarName'],
})
    .refine((v) => v.authCategory === 'connected-database' ||
    v.authMode === 'system' ||
    (v.baseUrl !== undefined && v.authMode !== undefined), {
    message: 'baseUrl and authMode are required for HTTP integrations',
    path: ['baseUrl'],
})
    .refine((v) => v.authCategory === 'connected-database' || !isConnectedDbAuthMode(v.authMode), {
    message: 'workload_identity, sql_password, and service_principal apply only to connected databases',
    path: ['authMode'],
})
    .refine((v) => v.authConfig?.token_endpoint_auth_method === undefined ||
    v.authMode === 'client_credentials' ||
    v.authMode === 'user_delegated', {
    message: 'token_endpoint_auth_method applies only to client_credentials and user_delegated',
    path: ['authConfig', 'token_endpoint_auth_method'],
})
    .refine((v) => v.authMode !== 'user_delegated' ||
    v.authConfig?.token_endpoint_auth_method === undefined ||
    v.authConfig.token_endpoint_auth_method === 'client_secret_post' ||
    v.authConfig.token_endpoint_auth_method === 'client_secret_basic', {
    message: 'user_delegated takes token_endpoint_auth_method client_secret_post or client_secret_basic',
    path: ['authConfig', 'token_endpoint_auth_method'],
})
    // A system integration is served by the control plane itself (ADR-0036): no
    // upstream base URL, no connection target, and always proxied.
    .refine((v) => v.authMode !== 'system' ||
    (v.baseUrl === undefined && v.connection === undefined && v.deliveryMode === 'proxied'), {
    message: 'a system integration has no baseUrl or connection and is always proxied',
    path: ['authMode'],
})
    .refine((v) => v.authCategory === 'connected-database' || v.connection === undefined, {
    message: 'connection applies only to connected databases',
    path: ['connection'],
})
    .refine((v) => v.authCategory !== 'connected-database' || v.connection !== undefined, {
    message: 'connection (engine/host/port/database) is required for a connected database',
    path: ['connection'],
})
    .refine((v) => v.authCategory !== 'connected-database' ||
    isConnectedDbAuthMode(v.authMode) ||
    v.authMode === 'user_delegated', {
    message: 'a connected database requires authMode workload_identity, sql_password, service_principal, or user_delegated',
    path: ['authMode'],
})
    .refine((v) => v.authCategory !== 'connected-database' ||
    v.deliveryMode === 'proxied' ||
    isInjectableConnectedDbAuthMode(v.authMode), {
    message: 'a connected database is delivered injected only with service_principal; every other mode is proxied',
    path: ['deliveryMode'],
})
    .refine((v) => v.authCategory !== 'connected-database' ||
    v.authMode !== 'user_delegated' ||
    USER_DELEGATED_DB_ENGINES.includes(v.connection?.engine ?? ''), {
    message: 'user_delegated applies to a connected database only on the fabric-warehouse engine',
    path: ['authMode'],
})
    .refine((v) => v.authCategory !== 'connected-database' ||
    v.deliveryMode !== 'injected' ||
    (v.envVarName?.length ?? 0) <= CONNECTED_DB_ENV_PREFIX_MAX_LENGTH, {
    message: `an injected connected database's envVarName is a prefix of at most ${CONNECTED_DB_ENV_PREFIX_MAX_LENGTH} characters`,
    path: ['envVarName'],
})
    .refine((v) => v.authCategory !== 'connected-database' || v.baseUrl === undefined, {
    message: 'a connected database has no baseUrl',
    path: ['baseUrl'],
})
    .refine((v) => v.authMode !== 'sql_password' || typeof v.authConfig?.['username'] === 'string', {
    message: 'sql_password requires authConfig.username',
    path: ['authConfig'],
})
    .refine((v) => v.authMode !== 'service_principal' ||
    (typeof v.authConfig?.['client_id'] === 'string' &&
        typeof v.authConfig?.['tenant_id'] === 'string'), {
    message: 'service_principal requires authConfig.client_id and authConfig.tenant_id',
    path: ['authConfig'],
})
    .refine((v) => {
    const allowed = connectedDbAuthConfigKeys(v.authCategory, v.authMode);
    return (allowed === undefined || Object.keys(v.authConfig ?? {}).every((k) => allowed.includes(k)));
}, {
    message: 'a connected-database authConfig carries only mode params (sql_password: username; service_principal: client_id + tenant_id; workload_identity: none; user_delegated: mechanism, authorize_url, token_url, revoke_url, scopes, token_endpoint_auth_method) — secrets go in credentials[].secret',
    path: ['authConfig'],
})
    .refine((v) => isSecretlessAuthMode(v.authMode)
    ? v.credentials.every((c) => c.secret === undefined)
    : v.credentials.every((c) => c.secret !== undefined), {
    message: 'credentials carry a secret for every auth mode except the secretless ones — workload_identity and system — which must not (service_principal → the client secret)',
    path: ['credentials'],
})
    // User passthrough is proxied by construction: one pod serves many users and an
    // env var holds one value (docs/48 §The core).
    .refine((v) => v.authMode !== 'user_delegated' || v.deliveryMode === 'proxied', {
    message: 'a user_delegated integration is proxied — its per-user tokens cannot be injected',
    path: ['deliveryMode'],
})
    .refine((v) => v.authMode !== 'user_delegated' || v.authConfig?.mechanism !== undefined, {
    message: 'user_delegated requires authConfig.mechanism',
    path: ['authConfig', 'mechanism'],
})
    .refine((v) => v.authMode === 'user_delegated' || v.authConfig?.mechanism === undefined, {
    message: 'authConfig.mechanism applies only to user_delegated',
    path: ['authConfig', 'mechanism'],
})
    // The credential names the upstream OAuth client it presents on user
    // passthrough and nothing else carries one (docs/15 §integration_credentials).
    .refine((v) => v.authMode === 'user_delegated'
    ? v.credentials.every((c) => c.authConfig !== undefined)
    : v.credentials.every((c) => c.authConfig === undefined), {
    message: 'credentials carry authConfig.client_id on a user_delegated integration and on no other mode',
    path: ['credentials'],
})
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.credentials.forEach((credential, index) => {
        if (seen.has(credential.slug)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['credentials', index, 'slug'],
                message: `duplicate credential slug "${credential.slug}"`,
            });
        }
        seen.add(credential.slug);
    });
});
// Edit an existing integration's editable fields in place (docs/09 §
// Integration Editing). Every field is optional — a partial patch — but the
// field set itself is closed: `name` (the manifest-facing slug), `authMode`,
// `engine`, `catalogKey`, and availability fields are deliberately absent, so setting any
// of them fails with an unrecognized-key error rather than a business-logic
// rejection (ADR-0039's closed-governance-surface posture, same as
// UpdateCredentialSchema leaving `slug` out entirely). Cross-field rules that
// depend on the integration's *current* row (e.g. envVarName's requirement
// tracks the effective delivery_mode, not just this patch) are enforced in the
// service, which has that state; this schema only enforces shape and that the
// patch isn't empty.
export const UpdateIntegrationMetadataSchema = z
    .object({
    displayName: z.string().min(1).max(255).optional(),
    baseUrl: BaseUrlSchema.optional(),
    allowInsecureHttp: z.boolean().optional(),
    // null returns the integration to verifying against the system roots.
    tlsConfig: IntegrationTlsConfigSchema.nullable().optional(),
    authConfig: AuthConfigSchema.optional(),
    deliveryMode: IntegrationDeliveryModeSchema.optional(),
    envVarName: EnvVarNameSchema.optional(),
    host: z.string().min(1).max(255).optional(),
    port: z.number().int().min(1).max(65535).optional(),
    databaseName: z.string().min(1).max(255).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).some((k) => k !== 'allowInsecureHttp'), {
    message: 'at least one field is required',
});
export const SetIntegrationAvailabilitySchema = z.strictObject({
    status: IntegrationStatusSchema.extract(['active', 'inactive']),
});
export const UpdateIntegrationSchema = z.union([
    SetIntegrationAvailabilitySchema,
    UpdateIntegrationMetadataSchema,
]);
export const AddCredentialSchema = CredentialInputSchema;
// Replace (rotate) the secret behind an existing credential. Write-only: the new
// value goes to the vault under the credential's stable secret name and never
// appears in the response, a log line, or the audit payload (docs/09 §
// Credential Rotation). Slug and scope are immutable here — only the secret moves.
export const ReplaceSecretSchema = z.object({
    secret: z.string().min(1).max(4096),
});
// Edit a credential's non-secret descriptive metadata in place. Slug is
// immutable (it is the handle grants bind on, is embedded in the vault key, and
// is referenced by live manifests' grants[].credential — docs/09 § Credential
// metadata editing), and the secret rotates through its own op, so the editable
// fields are the free-text `scope` and, on a user_delegated integration, the
// upstream client the credential presents. The governance field (approval_mode)
// moves through setCredentialPolicy.
export const UpdateCredentialSchema = z.object({
    scope: z.string().min(1).max(2000),
    authConfig: CredentialAuthConfigSchema.optional(),
});
// Strict: an unknown field fails validation rather than being silently
// stripped, so a caller can never believe it set a governance field the
// contract does not carry (ADR-0039).
export const SetCredentialPolicySchema = z.strictObject({
    approvalMode: IntegrationApprovalModeSchema,
});
// Replace the credential's group requirement wholesale; an empty list clears it.
export const SetCredentialRequiredGroupsSchema = z.strictObject({
    groups: z.array(CredentialRequiredGroupRefSchema).max(20),
});
// The pre-create reachability check the Register dialog runs (docs/09 §Upstream
// Reachability). It sends no credential, so it takes only the addresses.
export const UpstreamReachabilityRequestSchema = z
    .strictObject({
    baseUrl: BaseUrlSchema.optional(),
    tlsConfig: IntegrationTlsConfigSchema.nullable().optional(),
    // Matches AuthConfig's token_url, which a client-credentials IdP may serve over http.
    tokenUrl: z.string().url().max(2048).optional(),
    connection: z
        .strictObject({
        host: z.string().trim().min(1).max(253),
        port: z.number().int().min(1).max(65535),
    })
        .optional(),
})
    .refine((b) => b.baseUrl !== undefined || b.tokenUrl !== undefined || b.connection !== undefined, {
    message: 'Name at least one address to check.',
});
export const UpstreamReachabilityStageSchema = z.enum(['blocked', 'dns', 'tcp', 'tls', 'http']);
export const UpstreamReachabilityReportSchema = z.object({
    reachable: z.boolean(),
    egress: z.enum(['customer_firewall', 'cluster']),
    // What to do about a failure, worded for this install's egress; null when reachable.
    advice: z.string().nullable(),
    addresses: z.array(z.object({
        role: z.enum(['base_url', 'token_url', 'database']),
        host: z.string(),
        port: z.number().int(),
        reachable: z.boolean(),
        stage: UpstreamReachabilityStageSchema.nullable(),
        message: z.string().nullable(),
        // The https:// URL a plain-HTTP address redirects to on the same host; null otherwise.
        https_redirect: z.string().nullable(),
        // The TLS stage failed on a chain that did not verify, which a tls_config can fix.
        untrusted_chain: z.boolean(),
    })),
});
// A credential test makes one read-only call with the credential (docs/09
// §Testing a credential). The request path is what an HTTP test GETs under the
// base URL; it may carry a query string.
const CredentialTestPathSchema = z
    .string()
    .max(512)
    .regex(/^\/[^\s#]*$/, 'Must start with / and contain no spaces.');
export const DraftCredentialTestSchema = z.strictObject({
    catalogKey: z.string().max(63).optional(),
    baseUrl: BaseUrlSchema.optional(),
    allowInsecureHttp: z.boolean().optional(),
    tlsConfig: IntegrationTlsConfigSchema.nullable().optional(),
    authMode: IntegrationAuthModeSchema,
    authConfig: AuthConfigSchema.optional(),
    connection: ConnectedDbConnectionSchema.optional(),
    secret: z.string().min(1).max(16384).optional(),
    path: CredentialTestPathSchema.optional(),
});
export const CredentialTestRequestSchema = z.strictObject({
    path: CredentialTestPathSchema.optional(),
});
export const CredentialTestResultSchema = z.object({
    outcome: CredentialTestOutcomeSchema,
    checked_at: z.string().datetime({ offset: true }),
    latency_ms: z.number().int().nonnegative(),
    steps: z.array(z.object({
        step: z.enum(['network', 'credential', 'request']),
        status: z.enum(['ok', 'failed', 'skipped']),
        detail: z.string(),
    })),
    hint: z.string().nullable(),
    upstream_status: z.number().int().nullable(),
    // Field names and JSON types of the response body; never its values.
    response_shape: z.unknown(),
    scopes: z.array(z.string()).nullable(),
});
export const integrationsContract = c.router({
    // The curated in-repo integration catalog the registration picker reads (ADR-0026).
    // A release-pinned, read-only seed of tens of entries — returned whole (no pagination,
    // per docs/16). Distinct static path from `:id` routes, so no ordering conflict.
    catalog: {
        method: 'GET',
        path: '/api/integrations/catalog',
        responses: {
            200: CatalogListResponseSchema,
            ...errorResponses,
        },
        summary: 'List the integration catalog (curated registration entries).',
    },
    // Builder-facing available set (docs/06 listGrantableIntegrations). Distinct
    // static path from `:id` routes. Open to any signed-in role — the IT registry
    // list below stays governance-gated.
    grantable: {
        method: 'GET',
        path: '/api/integrations/grantable',
        query: PaginationQuerySchema,
        responses: {
            200: paginated(GrantableIntegrationSummarySchema),
            ...errorResponses,
        },
        summary: 'List org integrations grantable to apps (no secrets).',
    },
    // The connection-request queue (ADR-0026 §7): a citizen developer's agent
    // asking IT to connect a catalogued integration the org has not registered. These
    // live on the registry, not the Grants sub-view — a request for a *new* upstream
    // source is not a grant against an existing one. Read + decide only: the request
    // itself is filed through MCP/CLI, never a dashboard form (ADR-0029). Static paths,
    // declared before the `:id` routes.
    listConnectionRequests: {
        method: 'GET',
        path: '/api/integrations/connection-requests',
        query: PaginationQuerySchema.extend({ status: ConnectionRequestStatusSchema.optional() }),
        responses: {
            200: paginated(ConnectionRequestQueueItemSchema),
            ...errorResponses,
        },
        summary: 'List integration connection-requests awaiting or past IT review.',
    },
    // Records the decision only. The integration is registered through the existing
    // catalog Register flow, which the dashboard prefills from the approved request —
    // approving never connects anything by itself.
    approveConnectionRequest: {
        method: 'POST',
        path: '/api/integrations/connection-requests/:id/approve',
        pathParams: ConnectionRequestIdParamsSchema,
        body: ApproveConnectionRequestSchema,
        responses: {
            200: ConnectionRequestQueueItemSchema,
            ...errorResponses,
        },
        summary: 'Approve a connection-request and open the catalog Register flow.',
    },
    denyConnectionRequest: {
        method: 'POST',
        path: '/api/integrations/connection-requests/:id/deny',
        pathParams: ConnectionRequestIdParamsSchema,
        body: DenyConnectionRequestSchema,
        responses: {
            200: ConnectionRequestQueueItemSchema,
            ...errorResponses,
        },
        summary: 'Deny a connection-request, recording the reason.',
    },
    list: {
        method: 'GET',
        path: '/api/integrations',
        query: PaginationQuerySchema,
        responses: {
            200: paginated(IntegrationListItemSchema),
            ...errorResponses,
        },
        summary: 'List integrations registered in the org.',
    },
    create: {
        method: 'POST',
        path: '/api/integrations',
        body: CreateIntegrationSchema,
        responses: {
            201: IntegrationSchema,
            ...errorResponses,
        },
        summary: 'Register a new integration with its initial credential(s) (IT-admin only).',
    },
    testDraftCredential: {
        method: 'POST',
        path: '/api/integrations/credential-test',
        body: DraftCredentialTestSchema,
        responses: {
            200: CredentialTestResultSchema,
            ...errorResponses,
        },
        summary: 'Test a credential that has not been registered yet with one read-only call (IT-admin only).',
    },
    testCredential: {
        method: 'POST',
        path: '/api/integrations/:id/credentials/:slug/test',
        pathParams: CredentialSlugParamsSchema,
        body: CredentialTestRequestSchema,
        responses: {
            200: CredentialTestResultSchema,
            ...errorResponses,
        },
        summary: 'Test a saved credential with one read-only call and record the outcome (IT-admin only).',
    },
    checkReachability: {
        method: 'POST',
        path: '/api/integrations/reachability',
        body: UpstreamReachabilityRequestSchema,
        responses: {
            200: UpstreamReachabilityReportSchema,
            ...errorResponses,
        },
        summary: "Check that the control plane can reach an integration's upstream before registering it (IT-admin only).",
    },
    // Governed update of the integration record itself (docs/09 § Integration
    // Editing) — distinct from updateCredential below, which edits a credential's
    // scope. `name`, `authMode`, `engine`, and `catalogKey` are immutable and not
    // part of the body; a delivery_mode/env_var_name change never blocks on active
    // grants, it only reports them in the response for the caller to warn on.
    updateIntegration: {
        method: 'PATCH',
        path: '/api/integrations/:id',
        pathParams: IntegrationIdParamsSchema,
        body: UpdateIntegrationSchema,
        responses: {
            200: UpdateIntegrationResponseSchema,
            ...errorResponses,
        },
        summary: "Edit an integration's editable record fields; name/authMode/engine stay immutable (IT-admin only).",
    },
    addCredential: {
        method: 'POST',
        path: '/api/integrations/:id/credentials',
        pathParams: IntegrationIdParamsSchema,
        body: AddCredentialSchema,
        responses: {
            201: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: 'Add a credential to an existing integration (IT-admin only).',
    },
    replaceSecret: {
        method: 'PUT',
        path: '/api/integrations/:id/credentials/:slug/secret',
        pathParams: CredentialSlugParamsSchema,
        body: ReplaceSecretSchema,
        responses: {
            200: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: "Replace an existing credential's secret in place (IT-admin only).",
    },
    updateCredential: {
        method: 'PATCH',
        path: '/api/integrations/:id/credentials/:slug',
        pathParams: CredentialSlugParamsSchema,
        body: UpdateCredentialSchema,
        responses: {
            200: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: "Edit a credential's non-secret metadata (scope); slug is immutable (IT-admin only).",
    },
    setCredentialPolicy: {
        method: 'PATCH',
        path: '/api/integrations/:id/credentials/:slug/policy',
        pathParams: CredentialSlugParamsSchema,
        body: SetCredentialPolicySchema,
        responses: {
            200: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: "Set a credential's per-credential governance policy (IT-admin only).",
    },
    // Replacing the requirement re-runs group containment for every app granted
    // this credential (ADR-0029 amendment 2026-09-17).
    setCredentialRequiredGroups: {
        method: 'PUT',
        path: '/api/integrations/:id/credentials/:slug/required-groups',
        pathParams: CredentialSlugParamsSchema,
        body: SetCredentialRequiredGroupsSchema,
        responses: {
            200: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: 'Set the registered groups a credential requires; empty clears it (IT-admin only).',
    },
    // Guarded soft-delete: deactivates the credential (`is_active = false`) and
    // destroys its vault secret, but only once no pending/granted grant binds it —
    // otherwise 409 with the blocking apps in `details`. The row is never hard-deleted
    // (historical grant rows keep a resolvable FK target). docs/09 § Credential deletion.
    deleteCredential: {
        method: 'DELETE',
        path: '/api/integrations/:id/credentials/:slug',
        pathParams: CredentialSlugParamsSchema,
        body: c.noBody(),
        responses: {
            200: IntegrationCredentialSchema,
            ...errorResponses,
        },
        summary: 'Soft-delete a credential on an integration; refused while active grants bind it (IT-admin only).',
    },
    // Guarded soft-delete of the whole integration (the credential-delete pattern, one
    // level up): refused with a 409 listing the blocking apps while any pending/granted
    // grant binds it; otherwise the integration and all its credentials flip
    // `is_active = false`, every credential's vault secret is destroyed, and one audit
    // event records it. The row is never hard-deleted (historical grant rows keep a
    // resolvable FK target); the `(org, name)` unique index keeps the name reserved.
    // A manifest re-declaring the name then fails its next merge through the existing
    // "integration not registered" path. docs/09 § Integration deletion.
    deleteIntegration: {
        method: 'DELETE',
        path: '/api/integrations/:id',
        pathParams: IntegrationIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: IntegrationSchema,
            ...errorResponses,
        },
        summary: 'Soft-delete an integration and its credentials; refused while active grants bind it (IT-admin only).',
    },
});
//# sourceMappingURL=integrations.js.map